import './bootstrap';

/**
 * Site maintenance gate (mirrors App\Support\ProjectMaintenance + SiteMaintenanceMode).
 * If the server-rendered layout is the maintenance one, or the bootstrap data flags
 * `siteMaintenance: true` / `project.deployed === false` on production, we skip
 * mounting any Vue app/island and avoid every API call. The maintenance markup is
 * already in the DOM (server-rendered), so there's nothing for Vue to do here.
 */
function isSiteUnderMaintenance() {
    if (typeof window === 'undefined') return false;
    if (window.__MAINTENANCE_APPLIES__ === false) return false;
    const data = window.__APP_DATA__;
    if (!data) return false;
    if (data.siteMaintenance === true) return true;
    if (window.__MAINTENANCE_APPLIES__ === true && data.project?.deployed === false) return true;
    return false;
}
const siteUnderMaintenance = isSiteUnderMaintenance();

if (typeof window !== 'undefined') {
    window.tailwind = window.tailwind || {};
    window.tailwind.config = {
        darkMode: "class",
        theme: {
            extend: {
                colors: {
                    "brand-blue": "#11457E",
                    "pending-yellow": "#FDB022",
                    "pending-bg": "#FFFAEB",
                    "background-light": "#F8F9FA",
                    "dark-text": "#1A1A1A"
                },
                fontFamily: {
                    display: ["Public Sans", "sans-serif"]
                },
                borderRadius: {
                    DEFAULT: "0.25rem",
                    lg: "0.5rem",
                    xl: "0.75rem",
                    "2xl": "1rem"
                }
            }
        }
    };
}
import { createApp } from 'vue';
import store from './store';
import ShoppingCart from './components/ShoppingCart.vue';
import PricingSection from './components/PricingSection.vue';
import LanguageSelectorBlock from './components/LanguageSelectorBlock.vue';
import MobileHeader from './components/MobileHeader.vue';
import HeaderLanguageSelector from './components/HeaderLanguageSelector.vue';
import SelectAppCurrency from './components/inputs/SelectAppCurrency.vue';
import SvgIcon from './components/SvgIcon.vue';
import { asyncView } from './asyncComponent';
import { resolveHelpCenterUrl } from './shared/helpCenterResolver';

import router from './router';
import { startAttributionCaptureLifecycle, runAttributionCapture } from './attribution/capture.js';
import { setApiLanguageIdResolver } from './utils/apiLanguageId.js';
import vuetify from './vuetifyDatePicker.js';

const app = createApp({
    components: {
        SvgIcon,
    },
    data() {
        return {
            // Add your Vue data here
        }
    },
    mounted() {
        // initState runs before app.mount (see below) so child mounted() hooks see correct lang / langId for API calls.
        // Refetch /countries when UI language changes so labels match (store + localStorage stay in sync).
        this.$watch(
            () => this.$store.getters.lang?.id,
            (newId, oldId) => {
                if (oldId == null || newId == null || newId === oldId) return;
                if (typeof localStorage !== 'undefined') {
                    localStorage.setItem('langId', String(newId));
                }
                this.$store.dispatch('form/reloadCountries').catch(() => {});
            }
        );
    }
});

app.use(store);
app.use(router);
app.use(vuetify);

/** URL + GA cookies → cv_attribution_v1 + plain keys (gclid, client_id, …) for webhooks / api_tags */
startAttributionCaptureLifecycle();
router.afterEach(() => {
    runAttributionCapture();
});

const lazy = {
    paymentProcessing: asyncView(() => import('./components/PaymentProcessing.vue')),
    buyVignette: asyncView(() => import('./components/BuyVignette/BuyVignette.vue')),
    validityCheck: asyncView(() => import('./components/ValidityCheck.vue')),
    contactLanguages: asyncView(() => import('./components/ContactLanguages.vue')),
    paymentMethodsPills: asyncView(() => import('./components/PaymentMethodsPills.vue')),
    paymentMethodsCountrySelect: asyncView(() => import('./components/PaymentMethodsCountrySelect.vue')),
    paymentMethodsGrid: asyncView(() => import('./components/PaymentMethodsGrid.vue')),
    europeMap: asyncView(() => import('./components/EuropeMap.vue')),
    whyChooseUsLanguageDropdown: asyncView(() => import('./components/WhyChooseUsLanguageDropdown.vue')),
    team4Flags: asyncView(() => import('./components/Team4Flags.vue')),
    helpCenterLink: asyncView(() => import('./components/HelpCenterLink.vue')),
    homeLicensePlateSearch: asyncView(() => import('./components/HomeLicensePlateSearch.vue')),
    vignettePrices: asyncView(() => import('./components/VignettePrices.vue')),
};

app.component('payment-processing', lazy.paymentProcessing);
app.component('shopping-cart', ShoppingCart);
app.component('PricingSection', PricingSection);
app.component('pricing-section', PricingSection);
app.component('ValidityCheck', lazy.validityCheck);
app.component('validity-check', lazy.validityCheck);
app.component('ContactLanguages', lazy.contactLanguages);
app.component('contact-languages', lazy.contactLanguages);
app.component('PaymentMethodsPills', lazy.paymentMethodsPills);
app.component('payment-methods-pills', lazy.paymentMethodsPills);
app.component('PaymentMethodsCountrySelect', lazy.paymentMethodsCountrySelect);
app.component('payment-methods-country-select', lazy.paymentMethodsCountrySelect);
app.component('PaymentMethodsGrid', lazy.paymentMethodsGrid);
app.component('payment-methods-grid', lazy.paymentMethodsGrid);
app.component('EuropeMap', lazy.europeMap);
app.component('europe-map', lazy.europeMap);
app.component('LanguageSelectorBlock', LanguageSelectorBlock);
app.component('language-selector-block', LanguageSelectorBlock);
app.component('WhyChooseUsLanguageDropdown', lazy.whyChooseUsLanguageDropdown);
app.component('why-choose-us-language-dropdown', lazy.whyChooseUsLanguageDropdown);
app.component('Team4Flags', lazy.team4Flags);
app.component('team-4-flags', lazy.team4Flags);
app.component('HelpCenterLink', lazy.helpCenterLink);
app.component('help-center-link', lazy.helpCenterLink);
app.component('buy-vignette', lazy.buyVignette);
app.component('BuyVignette', lazy.buyVignette);
app.component('MobileHeader', MobileHeader);
app.component('mobile-header', MobileHeader);
app.component('HeaderLanguageSelector', HeaderLanguageSelector);
app.component('header-language-selector', HeaderLanguageSelector);
app.component('select-app-currency', SelectAppCurrency);
app.component('SelectAppCurrency', SelectAppCurrency);
app.component('SvgIcon', SvgIcon);
app.component('svg-icon', SvgIcon);
app.component('HomeLicensePlateSearch', lazy.homeLicensePlateSearch);
app.component('home-license-plate-search', lazy.homeLicensePlateSearch);
app.component('vignette-prices', lazy.vignettePrices);
app.component('VignettePrices', lazy.vignettePrices);

window.switchCurrency = function (currencyCode) {
    const code = String(currencyCode || '').toUpperCase();
    if (!code) return;
    store.dispatch('currency/setCurrencyByCode', code);

    if (typeof window.renderTable === 'function') {
        window.renderTable(code);
    }
};
window.getHelpCenterUrl = () => {
    return resolveHelpCenterUrl(window.APP_LANG);
};

function markVueIslandsReady(root) {
    const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
    scope.querySelectorAll('.vue-island:not([data-vue-ready])').forEach(function (el) {
        el.setAttribute('data-vue-ready', 'true');
    });
}

window.showCountryMethods = function (value) {
    const container = document.getElementById('country-methods');
    const note = document.querySelector('.country-note') || document.getElementById('no-country-note');
    const blocks = document.querySelectorAll('.country-content');
    if (!container) return;
    if (!value) {
        container.classList.add('hidden');
        if (note) note.classList.remove('hidden');
        blocks.forEach(function (el) { el.classList.add('hidden'); });
        return;
    }

    container.classList.remove('hidden');
    if (note) note.classList.add('hidden');

    blocks.forEach(function (el) {
        if (el.id === value + '-methods') {
            el.classList.remove('hidden');
        } else {
            el.classList.add('hidden');
        }
    });
};

/*
 * Must run before app.mount: Vue runs child mounted() before parent, so
 * loadInitialData would otherwise use stale localStorage.langId. The store
 * action reads `window.__APP_DATA__` (see store/index.js → bootstrapFromWindow and
 * resources/views/injections/body.blade.php), seeds
 * languages/countries/currencies and dispatches initState — no extra API
 * requests (/languages, /countries) on initial paint.
 *
 * On a maintenance render the SPA must not boot: there is no router state to
 * hydrate, no currency to switch, and no children to mount. We just flag the
 * document for maintenance styling and exit.
 */
if (!siteUnderMaintenance) {
    store.dispatch('bootstrapFromWindow');
    const bootstrapAppData = typeof window !== 'undefined' ? window.__APP_DATA__ : null;
    if (bootstrapAppData) {
        const initialCurrencyCode =
            store.getters['currency/currentCurrency']?.code || bootstrapAppData.currency?.code;
        if (initialCurrencyCode) {
            window.switchCurrency(initialCurrencyCode);
        }
    }

    setApiLanguageIdResolver(() => {
        const id = store.state.lang?.id;
        if (id !== undefined && id !== null && id !== '') return id;
        if (typeof localStorage !== 'undefined') {
            return localStorage.getItem('langId');
        }
        return null;
    });

    const pricingElForPrefetch = document.getElementById('pricing-app');
    if (pricingElForPrefetch?.dataset?.variant === 'hero') {
        store.dispatch('form/loadProductCatalog').catch(() => {});
    }

    app.mount('#app');
    markVueIslandsReady(document.getElementById('app'));
} else {
    document.documentElement.classList.add('app-ready', 'site-maintenance');
    if (document.body) document.body.classList.add('site-maintenance');
}

const pricingEl = document.getElementById('pricing-app');
if (pricingEl && !siteUnderMaintenance) {
    const pricingVariant = pricingEl.dataset.variant || null;
    const forceEuro = pricingEl.dataset.forceEuro === '1';
    const ctaTextKey = pricingEl.dataset.ctaTextKey || null;
    const standardPriceFromBlade = pricingEl.dataset.standardPrice || '';
    let sectionObjects = {};
    let sectionTexts = {};
    try {
        const raw = pricingEl.dataset.sectionObjects;
        if (raw) sectionObjects = JSON.parse(raw);
    } catch (_) {}
    try {
        const rawTexts = pricingEl.dataset.sectionTexts;
        if (rawTexts) sectionTexts = JSON.parse(rawTexts);
    } catch (_) {}
    const pricingApp = createApp({
        template: '<PricingSection :variant="variant" :force-euro="forceEuro" :cta-text-key="ctaTextKey" :section-objects="sectionObjects" :section-texts="sectionTexts" :standard-price-from-blade="standardPriceFromBlade" />',
        data: () => ({ variant: pricingVariant, forceEuro, ctaTextKey, sectionObjects, sectionTexts, standardPriceFromBlade })
    });
    pricingApp.use(store);
    pricingApp.component('PricingSection', PricingSection);
    // PricingSection uses <SvgIcon> in its template; child components do not auto-inherit
    // the parent app's globally registered components, so register it on this app instance.
    pricingApp.component('SvgIcon', SvgIcon);
    pricingApp.component('svg-icon', SvgIcon);
    pricingApp.mount('#pricing-app');
    markVueIslandsReady(document.getElementById('pricing-app')?.closest('.vue-island'));
}

const mapPopupEl = document.getElementById('map-popup-app');
if (mapPopupEl && !siteUnderMaintenance) {
    const imageSrc = mapPopupEl.getAttribute('data-image-src') || '';
    const caption = mapPopupEl.getAttribute('data-caption') || '';
    const mountMapPopup = () => {
        import('./components/MapPopup.vue').then(({ default: MapPopup }) => {
            if (mapPopupEl.__vue_app__) return;
            const mapPopupApp = createApp(MapPopup, { imageSrc, caption });
            mapPopupApp.use(store);
            mapPopupApp.component('SvgIcon', SvgIcon);
            mapPopupApp.component('svg-icon', SvgIcon);
            mapPopupApp.mount('#map-popup-app');
        });
    };

    if (typeof IntersectionObserver !== 'undefined') {
        const mapObserver = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
                mountMapPopup();
                mapObserver.disconnect();
            }
        }, { rootMargin: '240px' });
        mapObserver.observe(mapPopupEl);
    } else {
        mountMapPopup();
    }
}

/**
 * Legacy #mobile-header-app island (mobile header is now <mobile-header> inside #app).
 * Retry after load for Safari when defer app.js runs before window.__MOBILE_HEADER_DATA__.
 */
function mountMobileHeaderIsland() {
    if (siteUnderMaintenance) return;

    const tryMount = () => {
        const mobileHeaderEl = document.getElementById('mobile-header-app');
        const mobileProps = typeof window !== 'undefined' ? window.__MOBILE_HEADER_DATA__ : null;
        if (!mobileHeaderEl || !mobileProps || mobileHeaderEl.__vue_app__) {
            return Boolean(mobileHeaderEl?.__vue_app__);
        }

        const mobileHeaderApp = createApp(MobileHeader, mobileProps);
        mobileHeaderApp.use(store);
        mobileHeaderApp.mount('#mobile-header-app');
        markVueIslandsReady(mobileHeaderEl.closest('.vue-island'));
        return true;
    };

    if (tryMount()) return;

    const scheduleRetry = () => {
        if (tryMount()) return;
        window.addEventListener('load', () => tryMount(), { once: true });
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', scheduleRetry, { once: true });
    } else {
        scheduleRetry();
    }
}
mountMobileHeaderIsland();

const helpCenterLinkEl = document.getElementById('help-center-link');
if (helpCenterLinkEl && !siteUnderMaintenance) {
    const helpCenterLinkApp = createApp({});
    helpCenterLinkApp.use(store);
    helpCenterLinkApp.component('HelpCenterLink', HelpCenterLink);
    helpCenterLinkApp.component('help-center-link', HelpCenterLink);
    helpCenterLinkApp.mount('#help-center-link');
}

/**
 * Legacy hero-cart islands (empty #mobile-hero-cart-app divs). Mobile home/howtobuy
 * heroes now use <shopping-cart> inside #app like desktop. Keep this helper for any
 * page that still mounts a standalone cart and retry after load — Safari can run
 * deferred app.js before inline window.__*__ bootstrap scripts at </body>.
 */
function mountShoppingCartMini(mountId, dataKey) {
    if (siteUnderMaintenance) return;

    const tryMount = () => {
        const el = document.getElementById(mountId);
        const props = typeof window !== 'undefined' ? window[dataKey] : null;
        if (!el || !props || el.__vue_app__) return Boolean(el?.__vue_app__);

        const mini = createApp(ShoppingCart, props);
        mini.use(store);
        mini.use(vuetify);
        mini.mount(el);
        return true;
    };

    if (tryMount()) return;

    const scheduleRetry = () => {
        if (tryMount()) return;
        window.addEventListener('load', () => tryMount(), { once: true });
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', scheduleRetry, { once: true });
    } else {
        scheduleRetry();
    }
}
mountShoppingCartMini('mobile-hero-cart-app', '__MOBILE_HERO_CART__');
mountShoppingCartMini('howtobuy-hero-cart-app', '__HOWTOBUY_HERO_CART__');

/**
 * "Check your vignette" lead form rendered inside howitworks-section-4 (desktop + mobile).
 * Replaces the old single-dropdown island: now the whole form (country + plate + button)
 * is a single Vue app that builds a validity-check URL with `plate_country`, `license_plate`
 * and `auto=1` query params on submit. ValidityCheck.vue (the destination page) reads those
 * params and auto-runs the check, so the user lands directly on the result screen.
 */
const howitworksValidityFormEl = document.getElementById('howitworks-validity-form-app');
if (howitworksValidityFormEl && !siteUnderMaintenance) {
    /**
     * Decode JSON values pinned to data-* attributes (angle-down-solid, eu-stars).
     * Mirrors ValidityCheck.vue's parseJsonAttr — returns null for empty/'null'/invalid input.
     */
    const parseJsonAttr = (raw) => {
        if (raw == null) return null;
        const trimmed = String(raw).trim();
        if (!trimmed || trimmed === 'null') return null;
        try { return JSON.parse(trimmed); } catch (_) { return null; }
    };
    const formProps = {
        validityCheckUrl: howitworksValidityFormEl.dataset.validityCheckUrl || '',
        defaultCountry: howitworksValidityFormEl.dataset.defaultCountry || '',
        countryLabel: howitworksValidityFormEl.dataset.countryLabel || 'Country of Registration',
        plateLabel: howitworksValidityFormEl.dataset.plateLabel || 'License Plate Number',
        buttonLabel: howitworksValidityFormEl.dataset.buttonLabel || 'Check Vignette',
        platePlaceholder: howitworksValidityFormEl.dataset.platePlaceholder || 'e.g. 1AB 1234',
        angleDownSolid: parseJsonAttr(howitworksValidityFormEl.dataset.angleDownSolid),
        euStarsImage: parseJsonAttr(howitworksValidityFormEl.dataset.euStars),
    };
    import('./components/HowitworksValidityForm.vue').then(({ default: HowitworksValidityForm }) => {
        const howitworksFormApp = createApp(HowitworksValidityForm, formProps);
        howitworksFormApp.use(store);
        howitworksFormApp.component('SvgIcon', SvgIcon);
        howitworksFormApp.component('svg-icon', SvgIcon);
        /**
         * Load countries lazily once the form mounts — bootstrapFromWindow usually seeds them,
         * but if not we need /countries before DropdownField can render options.
         */
        if (!store.state?.form?.countries?.length) {
            store.dispatch('form/loadInitialData').catch(() => {});
        }
        howitworksFormApp.mount('#howitworks-validity-form-app');
    });
}

/* Editorial Policy & general content page behavior */
const TERMS_OF_USE_ANCHOR_OFFSET_PX = 90;

function scrollToInPageTarget(target, smooth) {
    if (!target) return;
    const termRoot = document.querySelector('.term-of-use-anchor-offset');
    if (termRoot && termRoot.contains(target)) {
        const y = target.getBoundingClientRect().top + window.scrollY - TERMS_OF_USE_ANCHOR_OFFSET_PX;
        window.scrollTo({ top: Math.max(0, y), behavior: smooth ? 'smooth' : 'auto' });
        return;
    }
    target.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
}

function scrollTermsOfUseHashFromLocation(smooth) {
    const hash = typeof location !== 'undefined' ? location.hash : '';
    if (!hash || hash === '#') return;
    let target;
    try {
        target = document.querySelector(hash);
    } catch (err) {
        return;
    }
    scrollToInPageTarget(target, smooth);
}

function initHeaderDropdowns() {
    const header = document.querySelector('header');
    if (!header) return;
    const nav = header.querySelector('nav');
    if (!nav) return;

    function closeAllDropdowns() {
        nav.querySelectorAll('details').forEach(function (d) { d.removeAttribute('open'); });
    }

    document.addEventListener('click', function (e) {
        const summary = e.target.closest('summary');
        const insideNav = e.target.closest('header nav');

        if (summary && summary.closest('nav') === nav) {
            e.preventDefault();
            e.stopPropagation();
            const clickedDetails = summary.closest('details');
            const wasOpen = clickedDetails.hasAttribute('open');
            closeAllDropdowns();
            if (!wasOpen) clickedDetails.setAttribute('open', '');
            return;
        }

        if (!insideNav) {
            closeAllDropdowns();
        }
    }, true);
}

function initFaqAccordion() {
    if (window.__faqAccordionBound) return;
    window.__faqAccordionBound = true;
    document.addEventListener('toggle', function (event) {
        const details = event.target;
        if (!(details instanceof HTMLDetailsElement)) return;
        if (!details.hasAttribute('data-faq-item')) return;
        if (!details.open) return;
        const root = details.closest('[data-faq-root]');
        if (!root) return;
        root.querySelectorAll('details[data-faq-item][open]').forEach(function (other) {
            if (other !== details) other.open = false;
        });
    }, true);
}

document.addEventListener('DOMContentLoaded', function () {
    initHeaderDropdowns();
    initFaqAccordion();

    requestAnimationFrame(function () {
        scrollTermsOfUseHashFromLocation(false);
    });

    window.addEventListener('hashchange', function () {
        scrollTermsOfUseHashFromLocation(true);
    });

    document.querySelectorAll('a[href^="#"]').forEach(function (anchor) {
        anchor.addEventListener('click', function (e) {
            const href = this.getAttribute('href');
            if (href === '#') return;
            e.preventDefault();
            const target = document.querySelector(href);
            scrollToInPageTarget(target, true);
        });
    });
    document.querySelectorAll('form[data-no-submit]').forEach(function (form) {
        form.addEventListener('submit', function (e) { e.preventDefault(); });
    });
});

window.UIUtils = window.UIUtils || {};
window.UIUtils.toggleElement = function (selector) {
    const el = document.querySelector(selector);
    if (el) el.style.display = el.style.display === 'none' ? '' : 'none';
};

// Article detail helpfulness widget
window.handleFeedback = function (type) {
    const yesBtn = document.getElementById('btn-yes');
    const noBtn = document.getElementById('btn-no');
    const thanks = document.getElementById('feedback-thanks');

    if (!yesBtn || !noBtn || !thanks) return;

    yesBtn.classList.remove('border-teal-400', 'bg-teal-50', 'text-teal-700');
    noBtn.classList.remove('border-red-300', 'bg-red-50', 'text-red-600');

    if (type === 'yes') {
        yesBtn.classList.add('border-teal-400', 'bg-teal-50', 'text-teal-700');
    } else {
        noBtn.classList.add('border-red-300', 'bg-red-50', 'text-red-600');
    }

    thanks.classList.remove('hidden');
};

// Submit request page interactions
window.updateCharCount = function (inputId, countId, max) {
    const input = document.getElementById(inputId);
    const counter = document.getElementById(countId);
    if (!input || !counter) return;

    const len = input.value.length;
    counter.textContent = `${len} / ${max}`;
    if (len > max * 0.9) {
        counter.classList.add('text-amber-500');
        counter.classList.remove('text-gray-400');
    } else {
        counter.classList.remove('text-amber-500');
        counter.classList.add('text-gray-400');
    }
};

window.handleDragOver = function (e) {
    e.preventDefault();
    const zone = document.getElementById('upload-zone');
    if (!zone) return;
    zone.style.borderColor = '#1e3a8a';
    zone.style.backgroundColor = '#eff6ff';
};

window.handleDragLeave = function () {
    const zone = document.getElementById('upload-zone');
    if (!zone) return;
    zone.style.borderColor = '#d1d5db';
    zone.style.backgroundColor = '';
};

window.renderFileList = function (files) {
    const list = document.getElementById('file-list');
    if (!list) return;

    list.innerHTML = '';
    if (!files || files.length === 0) {
        list.classList.add('hidden');
        return;
    }

    list.classList.remove('hidden');
    Array.from(files).forEach(function (file) {
        const parts = String(file.name || '').split('.');
        const ext = (parts.length > 1 ? parts.pop() : '').toUpperCase();
        const size = `${(file.size / 1024).toFixed(0)} KB`;
        const item = document.createElement('div');
        item.className = 'flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-small px-4 py-2.5';
        item.innerHTML =
            '<img src="/img/svg/ms/attach_file.svg" alt="" class="text-blue-900 w-[1.25rem] h-[1.25rem] object-contain shrink-0" onerror="if(!this.dataset.fb){this.dataset.fb=1;this.src=\'/img/svg/ms/help.svg\';}" />' +
            `<span class="text-sm font-medium text-gray-800 flex-1 truncate">${file.name}</span>` +
            `<span class="text-xs text-gray-400 flex-shrink-0">${size}</span>` +
            `<span class="text-xs font-bold text-blue-900 bg-blue-50 border border-blue-100 rounded-small px-2 py-0.5 flex-shrink-0">${ext}</span>`;
        list.appendChild(item);
    });
};

window.handleDrop = function (e) {
    e.preventDefault();
    window.handleDragLeave();
    const files = e?.dataTransfer?.files;
    window.renderFileList(files);
};

window.handleFileSelect = function (e) {
    window.renderFileList(e?.target?.files);
};

window.handleSubmit = function (e) {
    if (e) e.preventDefault();
    const btn = document.getElementById('submit-btn');
    if (!btn) return;

    btn.innerHTML = '<img src="/img/svg/ms/progress_activity.svg" alt="" class="inline-block w-[1.25rem] h-[1.25rem] object-contain animate-spin [animation-duration:2s]" onerror="if(!this.dataset.fb){this.dataset.fb=1;this.src=\'/img/svg/ms/help.svg\';}" /> Submitting...';
    btn.disabled = true;
    btn.classList.add('opacity-75');

    setTimeout(function () {
        window.location.href = '/api/copilot/prototype/5508d1fc-ea64-40f4-b105-774c5128c407/request-confirmation';
    }, 1200);
};

document.addEventListener('DOMContentLoaded', function () {
    const consent = document.getElementById('consent');
    if (!consent) return;

    consent.addEventListener('change', function () {
        const icon = document.getElementById('check-icon');
        if (!icon) return;
        if (this.checked) icon.classList.remove('hidden');
        else icon.classList.add('hidden');
    });
});

// Vignette prices table & FAQ interactions (vignette-prices section only)
(function () {
    if (typeof window === 'undefined') return;

    const rates = {
        CZK: { symbol: 'Kč', rate: 1,      decimals: 0 },
        EUR: { symbol: '€',  rate: 0.041,  decimals: 2 },
        GBP: { symbol: '£',  rate: 0.035,  decimals: 2 },
        USD: { symbol: '$',  rate: 0.044,  decimals: 2 },
        PLN: { symbol: 'zł', rate: 0.178,  decimals: 2 },
        HUF: { symbol: 'Ft', rate: 16.2,   decimals: 0 },
        CHF: { symbol: 'Fr', rate: 0.040,  decimals: 2 },
    };

    const products = [
        { validity: '1-Day',    icon: 'today',              officialCZK: 200,  feeCZK: 49 },
        { validity: '10-Day',   icon: 'calendar_view_week', officialCZK: 310,  feeCZK: 49 },
        { validity: '30-Day',   icon: 'calendar_month',     officialCZK: 440,  feeCZK: 49 },
        { validity: 'Annual',   icon: 'event_repeat',       officialCZK: 1500, feeCZK: 49 },
    ];

    function fmt(value, currency) {
        const info = rates[currency];
        if (!info) return String(value);
        const { symbol, rate, decimals } = info;
        const converted = value * rate;
        const formatted = converted
            .toFixed(decimals)
            .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return `${symbol}${formatted}`;
    }

    function renderTable(currency) {
        const tbody = document.getElementById('pricing-table-body');
        if (!tbody) return;

        tbody.innerHTML = '';
        products.forEach((p, i) => {
            const total = p.officialCZK + p.feeCZK;
            const isLast = i === products.length - 1;
            const borderClass = isLast ? '' : 'border-b border-gray-100';
            const row = `
        <tr class="${borderClass} hover:bg-gray-50 transition-colors">
          <td class="px-6 py-5">
            <div class="flex items-center gap-3">
              <div class="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center shrink-0">
                <img src="/img/svg/ms/${p.icon}.svg" alt="" class="w-5 h-5 object-contain" onerror="if(!this.dataset.fb){this.dataset.fb=1;this.src='/img/svg/ms/help.svg';}" />
              </div>
              <span class="font-bold text-gray-900">${p.validity}</span>
            </div>
          </td>
          <td class="px-6 py-5 text-right text-gray-600 font-medium">${fmt(p.officialCZK, currency)}</td>
          <td class="px-6 py-5 text-right text-gray-600 font-medium">${fmt(p.feeCZK, currency)}</td>
          <td class="px-6 py-5 text-right bg-blue-50">
            <span class="font-black text-blue-900 text-base">${fmt(total, currency)}</span>
          </td>
          <td class="px-6 py-5 text-right">
            <button class="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-small font-bold text-xs transition-all whitespace-nowrap">
              Buy Now
            </button>
          </td>
        </tr>`;
            tbody.insertAdjacentHTML('beforeend', row);
        });
    }

    window.renderTable = renderTable;

    window.toggleFaq = function (btn) {
        if (!btn) return;
        const body = btn.nextElementSibling;
        const icon = btn.querySelector('img[src*="/img/svg/ms/"]') || btn.querySelector('img');
        if (!body || !icon) return;

        const isOpen = !body.classList.contains('hidden');
        if (isOpen) {
            body.classList.add('hidden');
            icon.style.transform = 'rotate(0deg)';
        } else {
            body.classList.remove('hidden');
            icon.style.transform = 'rotate(180deg)';
        }
    };

    document.addEventListener('DOMContentLoaded', function () {
        if (document.getElementById('pricing-table-body')) {
            const code = (
                store.getters['currency/currentCurrency']?.code
                || 'CZK'
            );
            if (typeof window.switchCurrency === 'function') {
                window.switchCurrency(code);
            } else {
                renderTable(code);
            }
        }
    });
})();

